import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Camera, BarChart3, TrendingUp, BookOpen, MessageCircle, ArrowRight, X, Check } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const TOUR_SLIDES = [
  {
    id: 'snap',
    icon: Camera,
    title: 'Snap a photo of any meal',
    body: 'Nuri identifies every ingredient in under 3 seconds and asks smart follow-up questions to catch hidden calories.',
  },
  {
    id: 'macros',
    icon: BarChart3,
    title: 'Get accurate macros',
    body: 'AI-powered follow-up questions catch 200-500 hidden calories other apps miss. Cooking oil, dressings, portion sizes — nothing slips through.',
  },
  {
    id: 'progress',
    icon: TrendingUp,
    title: 'Track your progress',
    body: 'See your daily macros, weekly trends, and personalized insights that adapt to your health mode and goals.',
  },
  {
    id: 'recipes',
    icon: BookOpen,
    title: 'Discover 680+ recipes',
    body: 'Browse curated recipes filtered by your dietary needs — EDS-MCAS, Low-FODMAP, Keto, Paleo, and more.',
  },
  {
    id: 'ready',
    icon: Check,
    title: "You're ready",
    body: "Let's log your first meal. Snap a photo, use your voice, or just tell Nuri what you ate.",
    isFinal: true,
  },
];

export function FeatureTour({ onComplete }) {
  const [current, setCurrent] = useState(0);
  const slide = TOUR_SLIDES[current];
  const Icon = slide.icon;
  const isLast = current === TOUR_SLIDES.length - 1;

  return (
    <div data-testid="feature-tour" style={{
      position: 'fixed', inset: 0, zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    }}>
      <div style={{ width: '100%', maxWidth: 400, margin: '0 16px', position: 'relative' }}>
        {/* Skip */}
        <button data-testid="tour-skip" onClick={onComplete}
          style={{
            position: 'absolute', top: -36, right: 0,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, color: '#9A9590', fontFamily: "'DM Sans', sans-serif",
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
          Skip tour <X style={{ width: 12, height: 12 }} />
        </button>

        {/* Card */}
        <div style={{
          borderRadius: 16, background: '#FFFFFF',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}>
          {/* Icon area */}
          <div style={{
            height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#FDFBF7',
          }}>
            <div style={{
              width: 80, height: 80, borderRadius: 20,
              background: 'rgba(198,122,92,0.08)',
              border: '1.5px solid rgba(198,122,92,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon style={{ width: 36, height: 36, color: '#C67A5C', strokeWidth: 1.5 }} />
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '24px 24px 16px' }}>
            <h2 data-testid="tour-title" style={{
              fontSize: 22, fontWeight: 700, color: '#2D2D2D',
              fontFamily: "'Playfair Display', serif", margin: '0 0 8px',
              lineHeight: 1.3,
            }}>
              {slide.title}
            </h2>
            <p style={{
              fontSize: 14, color: '#6B6B6B', lineHeight: 1.6,
              fontFamily: "'DM Sans', sans-serif", margin: 0,
            }}>
              {slide.body}
            </p>
          </div>

          {/* Dots + CTA */}
          <div style={{ padding: '0 24px 24px' }}>
            {/* Dots */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8, marginBottom: 16,
            }}>
              {TOUR_SLIDES.map((_, i) => (
                <div key={i} data-testid={`tour-dot-${i}`} style={{
                  width: i === current ? 24 : 8, height: 8,
                  borderRadius: 4,
                  background: i === current ? '#C67A5C' : '#E0D6CC',
                  transition: 'all 0.3s ease',
                }} />
              ))}
            </div>

            {/* CTA button */}
            <button data-testid="tour-next" onClick={() => {
              if (isLast) onComplete();
              else setCurrent(c => c + 1);
            }}
              style={{
                width: '100%', height: 48, borderRadius: 12,
                background: '#C67A5C', color: '#FFFFFF', border: 'none',
                fontSize: 15, fontWeight: 600, cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'opacity 0.15s',
              }}
              onMouseOver={e => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseOut={e => { e.currentTarget.style.opacity = '1'; }}
            >
              {isLast ? 'Get Started' : 'Next'}
              <ArrowRight style={{ width: 16, height: 16 }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * useFeatureTour — Manages tour state and feature usage tracking.
 */
export function useFeatureTour() {
  const { authHeaders, user } = useAuth();
  const [showTour, setShowTour] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(true);

  useEffect(() => {
    if (!user) return;
    const checkTour = async () => {
      try {
        const res = await axios.get(`${API}/feature-tour/status`, authHeaders());
        const completed = res.data.tour_completed || false;
        setTourCompleted(completed);
        if (!completed && user.onboarding_complete) {
          setShowTour(true);
        }
      } catch {
        setTourCompleted(true);
      }
    };
    checkTour();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const completeTour = async () => {
    setShowTour(false);
    setTourCompleted(true);
    try {
      await axios.post(`${API}/feature-tour/complete`, {}, authHeaders());
    } catch {}
  };

  return { showTour, tourCompleted, completeTour };
}
